import React, { useState, useEffect } from 'react';
import {
  Brain,
  Users,
  FileText,
  BarChart3,
  Play,
  CheckCircle,
  AlertCircle,
  Clock,
  Target,
  TrendingUp,
  Loader2
} from 'lucide-react';
import api from '../../services/api';

const AITrainingPanel = () => {
  const [dashboard, setDashboard] = useState(null);
  const [contactMatchingResults, setContactMatchingResults] = useState(null);
  const [practiceAreaData, setPracticeAreaData] = useState(null);
  const [activeTraining, setActiveTraining] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    loadContactMatchingResults();
    loadPracticeAreaData();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/ai-training/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading AI training dashboard:', error);
    }
  };

  const loadContactMatchingResults = async () => {
    try {
      const response = await api.get('/ai-training/contact-matching/results');
      setContactMatchingResults(response.data);
    } catch (error) {
      console.error('Error loading contact matching results:', error);
    }
  };

  const loadPracticeAreaData = async () => {
    try {
      const response = await api.get('/ai-training/practice-areas/specialization');
      setPracticeAreaData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading practice area data:', error);
      setLoading(false);
    }
  };

  const startContactMatching = async () => {
    try {
      const response = await api.post('/ai-training/contact-matching/start', {
        limit: 30,
        enhanced: true
      });

      setActiveTraining({
        task_id: response.data.task_id,
        type: 'contact_matching',
        start_time: new Date()
      });

      pollTrainingStatus(response.data.task_id);
    } catch (error) {
      console.error('Error starting contact matching:', error);
    }
  };

  const pollTrainingStatus = async (taskId) => {
    const poll = async () => {
      try {
        const response = await api.get(`/ai-training/training-status/${taskId}`);
        const status = response.data;

        if (status.status === 'completed') {
          setActiveTraining(null);
          loadContactMatchingResults();
        } else if (status.status === 'failed') {
          setActiveTraining(null);
          console.error('Training failed:', status.error);
        } else {
          setTimeout(poll, 5000);
        }
      } catch (error) {
        console.error('Error polling training status:', error);
        setActiveTraining(null);
      }
    };

    setTimeout(poll, 2000);
  };

  const submitFeedback = async (matchId, verdict, reasoning) => {
    try {
      await api.post('/ai-training/feedback/submit', {
        match_id: matchId,
        verdict: verdict,
        reasoning: reasoning
      });
      loadContactMatchingResults();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-cyan-400" />
          <p className="text-slate-400">Loading AI Training Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
            <Brain className="h-7 w-7 text-cyan-400" />
            Astraea AI Training Center
          </h1>
          <p className="text-slate-400 mt-1">
            Train and improve Astraea's legal AI capabilities
          </p>
        </div>
      </div>

      {/* Training Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Training Sessions</p>
              <p className="text-2xl font-bold text-white">
                {dashboard?.feedback_history?.length || 0}
              </p>
            </div>
            <Target className="h-8 w-8 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Average Accuracy</p>
              <p className="text-2xl font-bold text-white">
                {dashboard?.feedback_history?.length > 0
                  ? Math.round(dashboard.feedback_history.reduce((acc, f) => acc + (f.accuracy || 0), 0) / dashboard.feedback_history.length)
                  : 0}%
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Practice Areas</p>
              <p className="text-2xl font-bold text-white">
                {practiceAreaData?.total_practice_areas || 0}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-cyan-400" />
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Active Training</p>
              <p className="text-2xl font-bold text-white">
                {activeTraining ? 1 : 0}
              </p>
            </div>
            <Clock className="h-8 w-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Active Training Status */}
      {activeTraining && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-orange-400" />
            <span className="font-medium text-white">Training in Progress:</span>
            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-sm rounded">Contact-Matter Matching</span>
            <span className="text-sm text-slate-400">
              Started {activeTraining.start_time.toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}

      {/* Training Modules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Contact-Matter Matching */}
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="p-4 border-b border-cyan-500/20">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
              <Users className="h-5 w-5 text-cyan-400" />
              Contact-Matter Matching
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Train Astraea to intelligently match contacts with legal matters
            </p>
          </div>
          <div className="p-4 space-y-4">
            {contactMatchingResults && (
              <div className="bg-slate-900/50 p-3 rounded-lg">
                <h4 className="font-medium mb-2 text-white">Latest Results:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                  <div>Total Matches: {contactMatchingResults.summary?.total_matches || 0}</div>
                  <div>High Confidence: {contactMatchingResults.summary?.high_confidence || 0}</div>
                  <div>Document Evidence: {contactMatchingResults.summary?.document_evidence || 0}</div>
                  <div>Needs Review: {contactMatchingResults.summary?.needs_review || 0}</div>
                </div>
              </div>
            )}

            <button
              onClick={startContactMatching}
              disabled={!!activeTraining}
              className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Play className="h-4 w-4" />
              Start Enhanced Matching Training
            </button>
          </div>
        </div>

        {/* Document Analysis */}
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="p-4 border-b border-cyan-500/20">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
              <FileText className="h-5 w-5 text-cyan-400" />
              Document Analysis
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Improve Astraea's document classification and analysis
            </p>
          </div>
          <div className="p-4 space-y-4">
            <div className="bg-slate-900/50 p-3 rounded-lg">
              <h4 className="font-medium mb-2 text-white">Analysis Status:</h4>
              <div className="text-sm text-slate-400">
                7,930 documents analyzed with AI categorization
              </div>
            </div>

            <button className="w-full px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 border border-slate-600 flex items-center justify-center gap-2">
              <FileText className="h-4 w-4" />
              Review Document Classifications
            </button>
          </div>
        </div>
      </div>

      {/* Match Review Interface */}
      {contactMatchingResults?.results && contactMatchingResults.results.length > 0 && (
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="p-4 border-b border-cyan-500/20">
            <h3 className="text-lg font-semibold text-white">Review Astraea's Matching Decisions</h3>
            <p className="text-slate-400 text-sm mt-1">
              Provide feedback to improve future matching accuracy
            </p>
          </div>
          <div className="p-4">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {contactMatchingResults.results.slice(0, 10).map((match, index) => (
                <MatchReviewCard
                  key={match.id || index}
                  match={match}
                  onFeedback={submitFeedback}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Practice Area Specialization */}
      {practiceAreaData?.specializations && practiceAreaData.specializations.length > 0 && (
        <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
          <div className="p-4 border-b border-cyan-500/20">
            <h3 className="text-lg font-semibold text-white">AI Expertise by Practice Area</h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {practiceAreaData.specializations.map((area, index) => (
                <div key={index} className="bg-slate-900/50 p-3 rounded-lg">
                  <h4 className="font-medium text-white">{area.practice_area}</h4>
                  <div className="text-sm text-slate-400 space-y-1 mt-2">
                    <div>{area.documents_analyzed} documents analyzed</div>
                    <div>{area.cases_analyzed} cases processed</div>
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs rounded ${
                      area.ai_expertise_level === 'Advanced'
                        ? 'bg-green-500/20 text-green-300'
                        : 'bg-slate-600 text-slate-300'
                    }`}>
                      {area.ai_expertise_level}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MatchReviewCard = ({ match, onFeedback }) => {
  const [feedback, setFeedback] = useState('');
  const [verdict, setVerdict] = useState(null);

  const handleSubmit = () => {
    if (verdict) {
      onFeedback(match.id, verdict, feedback);
      setVerdict(null);
      setFeedback('');
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'text-green-400 bg-green-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  return (
    <div className="border border-slate-700 rounded-lg p-4 space-y-3 bg-slate-900/30">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-white">
            {match.contact_name} → Case #{match.case_number}
          </h4>
          <p className="text-sm text-slate-400">{match.case_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-xs rounded ${getConfidenceColor(match.confidence)}`}>
            {match.confidence}
          </span>
          {match.document_mentions > 0 && (
            <span className="px-2 py-0.5 text-xs rounded bg-slate-600 text-slate-300">
              {match.document_mentions} doc mentions
            </span>
          )}
        </div>
      </div>

      <div className="text-sm text-slate-300">
        <p><strong className="text-slate-200">Relationship:</strong> {match.relationship_type}</p>
        <p><strong className="text-slate-200">Astraea's Reasoning:</strong> {match.reasoning}</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setVerdict('correct')}
          className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 ${
            verdict === 'correct'
              ? 'bg-green-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          <CheckCircle className="h-4 w-4" />
          Correct
        </button>
        <button
          onClick={() => setVerdict('incorrect')}
          className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 ${
            verdict === 'incorrect'
              ? 'bg-red-600 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          <AlertCircle className="h-4 w-4" />
          Incorrect
        </button>
        {verdict && (
          <button
            onClick={handleSubmit}
            className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Submit Feedback
          </button>
        )}
      </div>

      {verdict === 'incorrect' && (
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Why is this match incorrect? (optional)"
          className="w-full p-2 bg-slate-800 border border-slate-600 rounded text-sm text-white placeholder-slate-500"
          rows="2"
        />
      )}
    </div>
  );
};

export default AITrainingPanel;
